import { Address } from './address';
import { ItemStatus } from './item-status';
import { Participant } from './participant';
import { isNullOrUndefined } from "../utils";

/**
 *         "ContractData": {
 *             "required": ["contractNumber", "deliveryType", "from", "to", "isPreprint", "isPrintingRequired", "isBatchScanPossible"],
 *             "type": "object",
 *             "properties": {
 *                 "contractNumber": {
 *                     "description": "Auftragsnummer",
 *                     "type": "string"
 *                 },
 *                 "deliveryType": {
 *                     "description": "Abholung / Zustellung",
 *                     "enum": ["Pickup", "Handover"],
 *                     "type": "string"
 *                 },
 *                 "from": {
 *                     "$ref": "#/definitions/ParticipantData",
 *                     "description": "Von"
 *                 },
 *                 "to": {
 *                     "$ref": "#/definitions/ParticipantData",
 *                     "description": "Nach"
 *                 },
 *                 "isPreprint": {
 *                     "description": "Vordruck",
 *                     "type": "boolean"
 *                 },
 *                 "isPrintingRequired": {
 *                     "description": "Druck der Auftragsdokumente notwendig",
 *                     "type": "boolean"
 *                 },
 *                 "isBatchScanPossible": {
 *                     "description": "Batch-Scanning möglich",
 *                     "type": "boolean"
 *                 }
 *             }
 *         },
 */

type DeliveryKind = 'Pickup' | 'Handover';

// eslint-disable-next-line no-underscore-dangle, id-blacklist, id-match
const DeliveryKind = {
  Pickup: 'Pickup',
  Handover: 'Handover',
};

export class HomeDelivery {
  public status: ItemStatus = ItemStatus.empty;

  constructor(
    private json: any, // raw JSON representation for serialize/deserializion later
    public contractNumber: string,
    public kind: DeliveryKind,
    public origin: Participant,
    public destination: Participant,
    public isBatchScanPossible: boolean
  ) { }

  public static fromJSON(json: {
    contractNumber: string,
    deliveryType: string,
    // eslint-disable-next-line
    from: any,
    to: any,
    isBatchScanPossible: boolean
  }): HomeDelivery {
    if (!json) {
      throw new Error('HomeDelivery JSON should not be undefined');
    }
    if (!json.contractNumber) {
      throw new Error('HomeDelivery JSON should have property: contractNumber');
    }
    if (!json.deliveryType) {
      throw new Error('HomeDelivery JSON should have property: deliveryType');
    }
    if (!json.from) {
      throw new Error('HomeDelivery JSON should have property: from');
    }
    if (!json.to) {
      throw new Error('HomeDelivery JSON should have property: to');
    }
    if (isNullOrUndefined(json.isBatchScanPossible)) {
      throw new Error('HomeDelivery JSON should have property: isBatchScanPossible');
    }

    const origin = Participant.fromJSON(json.from);
    const destination = Participant.fromJSON(json.to);

    return new HomeDelivery(
      json,
      json.contractNumber,
      DeliveryKind[json.deliveryType],
      origin,
      destination,
      json.isBatchScanPossible
    );
  }

  public get isPickup(): boolean {
    return this.kind === DeliveryKind.Pickup;
  }

  public get isHandover(): boolean {
    return this.kind === DeliveryKind.Handover;
  }

  public get participant(): Participant {
    if (this.isPickup) {
      return this.origin;
    } else {
      return this.destination;
    }
  }

  public get secondaryParticipant(): Participant {
    if (this.isPickup) {
      return this.destination;
    } else {
      return this.origin;
    }
  }

  public get associatedName(): string {
    return this.participant.name;
  }

  public get address(): Address {
    return this.participant.address;
  }

  public get isComplete(): boolean {
    return this.status.isComplete;
  }

  public setItemStatus(newItemStatus: ItemStatus): HomeDelivery {
    this.status = newItemStatus;
    return this;
  }

  public get raw(): any {
    return this.json;
  }
}
